/**
 * Probation rules shared by the API (which enforces them) and the web panel (which renders them).
 *
 * Kept free of Zod and Node so it stays RN-safe; the Zod shapes that carry these values over the
 * wire live in `contracts/employee.contract.ts`.
 */

/**
 * The most APPROVED probation extensions one employment may accumulate. A `pending` request does
 * not count (it may still be rejected) but does block a second concurrent request. Per-organisation
 * limits are a follow-up — see ADR 0027 decision 8.
 */
export const MAX_PROBATION_EXTENSIONS = 2;

/**
 * Machine-readable `details.reason` on a 409 from the probation routes (`confirmProbation`,
 * `extendProbation`, `cancelProbationExtension`, and `updateStatus` for probation -> confirmed).
 * The HTTP `code` stays the generic `CONFLICT`; clients branch on this instead of parsing prose.
 */
export const PROBATION_CONFLICT_REASONS = [
  /** The employment is not currently on probation (wrong status, or no probation end date). */
  'not_on_probation',
  /** `MAX_PROBATION_EXTENSIONS` approved extensions already exist. */
  'extension_limit_reached',
  /** An extension request is already awaiting a decision. */
  'extension_pending',
  /** Confirmation is blocked by incomplete required onboarding tasks — `details.blockers` lists them. */
  'incomplete_required_tasks',
  /** The supplied `reviewId` is not a probation-cycle review of this employee. */
  'review_mismatch',
  /** The extension request is no longer pending (already decided or cancelled). */
  'extension_not_pending',
  /** Someone else changed the probation between the read and the write; reload and retry. */
  'concurrent_change',
] as const;
export type ProbationConflictReason = (typeof PROBATION_CONFLICT_REASONS)[number];
